/**
 * 招聘相关 API 接口
 */

import request from '../utils/request';

/**
 * 简历投递请求参数
 */
export interface ResumeSubmitParams {
  position: string;           // 岗位名称（对应接口 jobName）
  resumeFile: File;           // 简历文件（对应接口 file）
}

/**
 * 简历投递响应数据
 * 根据接口实际返回：{ "code": 200, "msg": "访问成功", "data": null, ... }
 */
export interface ResumeSubmitResponse {
  code: number;
  msg?: string;
  message?: string;
  data: any;
  pageSize?: null;
  pageNum?: null;
  total?: null;
  totalPage?: null;
}

/**
 * 投递简历
 * 接口地址：https://dafangyuntu.com/tool/sendEMail
 * 请求方式：POST multipart/form-data
 * 参数：file（简历文件）, jobName（岗位名称）
 * 
 * @param params 投递参数
 * @returns 投递结果
 * 
 * @example
 * ```typescript
 * const result = await submitResume({
 *   position: '高级Android开发工程师-车载',
 *   resumeFile: file,
 * });
 * console.log(result.msg); // "访问成功"
 * ```
 */
export const submitResume = async (
  params: ResumeSubmitParams
): Promise<ResumeSubmitResponse> => {
  try {
    // 创建 FormData 对象
    const formData = new FormData();
    
    // 按照接口要求添加字段：file 和 jobName
    formData.append('file', params.resumeFile);
    formData.append('title', params.position);

    // 直接使用底层 request 方法，FormData 会被自动处理（不设置 Content-Type）
    const response = await request<ResumeSubmitResponse>(
      'https://dafangyuntu.com/tool/sendEMail',
      {
        method: 'POST',
        body: formData,
        timeout: 30000, // 30秒超时，文件上传可能需要更长时间
      }
    );

    return response;
  } catch (error) {
    console.error('简历投递失败:', error);
    throw error;
  }
};

/**
 * 验证简历文件
 * 
 * @param file 文件对象
 * @returns 验证结果
 */
export const validateResumeFile = (file: File): { valid: boolean; error?: string } => {
  // 文件类型验证
  const validTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: '仅支持上传 PDF、Word 格式的简历',
    };
  }

  // 文件大小验证（10MB）
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      error: '文件大小不能超过 10MB',
    };
  }

  // 文件名验证（排除非法字符）
  const filenameRegex = /^[^<>:"/\\|?*\x00-\x1F]+$/;
  if (!filenameRegex.test(file.name)) {
    return {
      valid: false,
      error: '文件名包含非法字符',
    };
  }

  return { valid: true };
};

/**
 * 格式化文件大小
 * 
 * @param bytes 字节数
 * @returns 格式化后的字符串
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};